import { Icon, IconsEnum } from 'components/Icons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useFilteredAvvenimentoCount, useInfoTipoScommessaInformation, useTabList } from 'features/sport/hooks';

import { AmbienteContext } from 'context/Ambiente';
import { NextEventsProps } from '.';
import { NextEventsScommesseTable } from 'components/nextEventsScommesseTable';
import { ScommesseTableTabs } from 'components/scommesseTable/scommesseTableTabs';
import { ScommesseTableTitle } from 'components/scommesseTable/scommesseTableTitle';
import { SportManifestazioniTabType } from 'features/sport/components/SportManifestazioniTabs/SportManifestazioniTabs';
import { SportManifestazioniTabs } from 'features/sport/components/SportManifestazioniTabs';
import { SportTableScommesseHeadersSticky } from 'features/sport/components/sportTableScommesse/sportTableScommesseHeadersSticky';
import { SportsPointerAvvenimentoDto } from 'types/swagger';
import { TooltipPosition } from 'components/tooltip';
import { Wrapper } from 'components/wrapper';
import classNames from 'classnames';
import { getProssimiEventi } from 'features/sport';
import { isTruthy } from 'utility/functions';
import { selectIsOpenCarrello } from 'features/carrello/carrelloSelectors';
import styles from './NextEvents.module.scss';
import { useRouter } from 'next/router';
import { useTranslation } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';

const MyNextEvents = ({
  slug,
  title,
  hasFilter,
  removeTabs,
  hasPaddingLeft,
  noPaddingRight,
  isNotTopTemplate,
  handleOpenFilters,
}: NextEventsProps) => {
  const { t } = useTranslation();

  const isOpen = useTypedSelector(selectIsOpenCarrello);
  const { isMobile } = useWindowSize();

  const [currentTab, setCurrentTab] = useState<string>('1');

  const [selectedMobileTipoScommessa, setSelectedMobileTipoScommessa] = useState(0);

  const { tabList, count } = useTabList();

  const { selectedTab, infoTipoScommesseKeyListTab } = useMemo(() => {
    const tabIndex = +currentTab - 1;
    const selectedTab = tabList?.[tabIndex];
    const infoTipoScommesseKeyListTab = (selectedTab?.infoTipoScommessaKeyList ?? []).filter((x) => !!x);
    return { selectedTab, infoTipoScommesseKeyListTab };
  }, [currentTab, tabList]);

  const { keys } = useFilteredAvvenimentoCount();

  const { infoTipoScommessaList, infoTipoScommessaKeyList, infoTipoScommessaDescription } =
    useInfoTipoScommessaInformation(infoTipoScommesseKeyListTab);

  useEffect(() => {
    setCurrentTab('1');
  }, [slug]);

  const getCurrentTab = useCallback(
    (value: string) => {
      setCurrentTab(value);
      setSelectedMobileTipoScommessa(0);
    },
    [setCurrentTab]
  );

  if (!isTruthy(count) || !isTruthy(tabList?.length)) return null;

  return (
    <Wrapper
      noPaddingTop
      noPaddingLeft={isMobile || !hasPaddingLeft}
      noPaddingRight={noPaddingRight}
      classNameWrapper={styles.wrapper}
      bgColorTransparent
    >
      <div className={classNames(styles.container, { [styles.cartOpen]: isOpen })}>
        <div className={styles.titleContainer}>
          {title && <h2 className={styles.title}>{title}</h2>}
          {hasFilter && (
            <button
              type="button"
              className={styles.button}
              onClick={() => handleOpenFilters!((prevState) => !prevState)}
            >
              <span className={styles.label} suppressHydrationWarning={true}>
                {t('filtri')}
              </span>
              <Icon iconId={IconsEnum.FILTER} color="var(--color-on-bg-primary)" className={styles.iconFilter} />
            </button>
          )}
        </div>
        {/* sport Tabs  cambiare nome */}
        <SportManifestazioniTabs
          listTab={tabList.map(
            ({ idTab, textTab, textTabTrKey }) =>
              ({ idTab: `${idTab}`, textTab, textTabTrKey } as SportManifestazioniTabType)
          )}
          currentTab={currentTab}
          removeTabs={removeTabs ? true : false}
          handleClick={getCurrentTab}
        >
          <div className={styles.wrapperGroup} data-testid="NextEvents">
            {infoTipoScommessaDescription.length > 1 && (
              <ScommesseTableTabs
                activeIndex={selectedMobileTipoScommessa}
                descriptions={infoTipoScommessaDescription}
                onSelectedItem={setSelectedMobileTipoScommessa}
              />
            )}
            <SportTableScommesseHeadersSticky
              infoTipoScommessaKeyList={infoTipoScommessaKeyList}
              className={classNames({ [styles.hidden]: isMobile })}
            />
            {(selectedTab?.contentTabList ?? [])
              .filter((x) => {
                const lst = (x.avvenimentoList ?? []).filter(
                  (el, idx, lst) => idx === lst.findIndex((tmp) => tmp.key === el.key) && keys.includes(el.key)
                );
                return isTruthy(lst?.length);
              })
              .map((manifestazione, indexManifestazione) => {
                const isFirst = indexManifestazione < 1;
                const avvenimenti: Array<SportsPointerAvvenimentoDto> = (manifestazione?.avvenimentoList ?? [])
                  .filter((el, idx, lst) => idx === lst.findIndex((tmp) => tmp.key === el.key) && keys.includes(el.key))
                  .map(({ key, firstPriority }) => ({ key, firstPriority } as SportsPointerAvvenimentoDto));

                const { descrizione: labelSx, descrizioneTrKey: labelSxTrKey } = manifestazione ?? {};
                const { descrizione: labelDx, descrizioneTrKey: labelDxTrKey } =
                  infoTipoScommessaDescription?.[selectedMobileTipoScommessa] ?? {};

                return (
                  <div
                    key={`${manifestazione}_${indexManifestazione}`}
                    className={classNames(styles.wrapperIsVisible, { [styles.noMargin]: isFirst })}
                  >
                    <ScommesseTableTitle
                      labelSx={isNotTopTemplate ? '' : labelSx}
                      labelSxTrKey={isNotTopTemplate ? '' : labelSxTrKey}
                      labelDx={labelDx}
                      labelDxTrKey={labelDxTrKey}
                      tooltipText={
                        infoTipoScommessaList[indexManifestazione]?.toolTip &&
                        infoTipoScommessaList[indexManifestazione]?.toolTip
                      }
                      tooltipTextTrKey={
                        infoTipoScommessaList[indexManifestazione]?.toolTipTrKey &&
                        infoTipoScommessaList[indexManifestazione]?.toolTipTrKey
                      }
                      tooltipPosition={isMobile ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                      className={classNames({ [styles.hidden]: !isMobile })}
                    />
                    <NextEventsScommesseTable
                      title={labelSx}
                      className={styles.noMargin}
                      titleTrKey={labelSxTrKey}
                      avvenimenti={avvenimenti}
                      infoTipoScommessaList={infoTipoScommessaList}
                      descrizioni={infoTipoScommessaDescription}
                      showTitle={!isNotTopTemplate}
                      hasBorderSide
                      isTopTemplate={!isNotTopTemplate}
                      showTableTitle={isMobile ? false : isFirst}
                      hasHeaderScommessa={isFirst}
                      infoTipoScommessaKeyList={infoTipoScommessaKeyList}
                      selectedIndexInfoTipoScommessa={selectedMobileTipoScommessa}
                      showOnlyBilanciata
                    />
                  </div>
                );
              })}
          </div>
        </SportManifestazioniTabs>
      </div>
    </Wrapper>
  );
};

export const NextEvents = (props: NextEventsProps) => {
  const { slug } = props ?? {};
  const dispatch = useAppDispatch();
  const { isReady, isFallback, asPath } = useRouter();

  const slugRef = useRef<string>('');

  useEffect(() => {
    if (slugRef.current !== slug) {
      slugRef.current = slug;
      dispatch(getProssimiEventi({ slug, ts: new Date() }));
    }
  }, [slug, dispatch]);

  return (
    <AmbienteContext.Provider
      key={`content-next-events`}
      value={{
        ambiente: 'sport',
        pathScommessa: ['sport', 'templateProssimiEventi'],
      }}
    >
      <MyNextEvents {...props} key={`MyNextEvents-${isReady}-${isFallback}-${asPath}`} />
    </AmbienteContext.Provider>
  );
};
